import React, { useEffect, useState } from "react";
import { isEmpty, isNull } from "lodash";
import StatusChip from "@/app/features/tender/components/view/Status";
import QuotationDetailsModal, {
  QuotationType,
} from "../../responsiveSeller/Wedgits/QuotationDetailsModal";
import { CiLocationOn } from "react-icons/ci";
import citiesData from "@/app/features/company/data/cities_lite.json";

import { useMediaQuery } from "react-responsive";
import TableComponent from "@/app/global-components/table";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLobby from "@/hooks/use-lobby";
import { BiDollarCircle, BiTime, BiCalendar } from "react-icons/bi";

interface TenderQuotation {
  tender: { title: string; id: string; status: string; city: string };
  id: string;
  status: string;
  value: number;
  products: {
    quantity: number;
    unitPrice: number;
  }[];
  DeadLineDate: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const TrenderQuatationsList: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { getListtenderQuotations } = useLobby();

  const [listOffers, setListOffers] = useState<TenderQuotation[]>([]);
  const [selectedQuotation, setSelectedQuotation] =
    useState<QuotationType | null>(null);
  const [cities, setCities] = useState<Option[]>([]);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    setCities(
      citiesData.map((City: City) => ({
        value: City.city_id.toString(),
        label: City.name_ar,
      }))
    );

    const fetchQuotations = async () => {
      try {
        const response = await getListtenderQuotations();
        if (response) {
          setListOffers(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching quotations:", error);
      }
    };

    fetchQuotations();
  }, []);

  const handleViewDetails = (quotation: QuotationType) => {
    setSelectedQuotation(quotation);
  };

  const handleCloseDetailsModal = () => {
    setSelectedQuotation(null);
  };

  const getCity = (value: string) => {
    const city = cities.find(
      (item) => parseInt(item.value) === parseInt(value)
    );
    return city ? city.label : "";
  };

  const columns = [
    {
      title: (
        <div className="flex items-center gap-2">
          <BiCalendar className="w-4 h-4 text-blue-600" />
          {t("navigation.Opportunities")}
        </div>
      ),
      field: "tender.title",
      render: (row: TenderQuotation) => (
        <div className="font-medium text-gray-900">
          {row.tender.title || ""}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <BiTime className="w-4 h-4 text-orange-600" />
          {t("statistics.remainingToSubmit")}
        </div>
      ),
      field: "DeadLineDate",
      render: (row: TenderQuotation) => (
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
            {row.DeadLineDate ?? 0} {t("time.days")}
          </span>
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <BiDollarCircle className="w-4 h-4 text-green-600" />
          {t("tender.value")}
        </div>
      ),
      field: "value",
      render: (row: TenderQuotation) => (
        <div className="font-semibold text-green-700">
          {!isEmpty(row.products)
            ? row.products
                .reduce((acc, pro) => acc + pro.quantity * pro.unitPrice, 0)
                .toFixed(2)
            : "0.00"}{" "}
          {t("currency")}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <CiLocationOn className="w-4 h-4 text-purple-600" />
          {t("company.fields.city")}
        </div>
      ),
      field: "tender.city",
      render: (row: TenderQuotation) => (
        <div className="flex items-center gap-2">
          <CiLocationOn className="w-4 h-4 text-purple-500" />
          <span className="text-gray-700">{getCity(row.tender.city)}</span>
        </div>
      ),
      hideOnMobile: true,
      sortable: true,
    },
    {
      title: t("orders.fields.status"),
      field: "status",
      render: (row: TenderQuotation) => (
        <StatusChip status={isNull(row.status) ? "Opened" : row.status} />
      ),
      sortable: true,
    },
  ];

  return (
    <div className="p-4 mt-2">
      <TableComponent
        columns={columns}
        data={listOffers}
        rowKeyField="id"
        maxHeight={isMobile ? 500 : 400}
        isMobile={isMobile}
        onRowClick={handleViewDetails}
        title={t("offers.LatestPriceOffers")}
        subtitle={`${listOffers.length} ${t("statistics.quotationsAvailable")}`}
        searchable={true}
        sortable={true}
        variant="elevated"
        size="md"
        emptyStateMessage="No quotations found"
        emptyStateSubmessage="There are no quotations available at the moment"
      />

      {selectedQuotation && (
        <QuotationDetailsModal
          open={!!selectedQuotation}
          onClose={handleCloseDetailsModal}
          quotation={selectedQuotation}
          offerStatus={
            isNull(selectedQuotation.tender)
              ? ""
              : selectedQuotation.tender.status
          }
        />
      )}
    </div>
  );
};

export default TrenderQuatationsList;
